import { ClockTimestamp } from '@sqior/js/data';
import { ComponentFactory } from '@sqior/react/factory';
import { OperationContext } from '@sqior/react/operation';
import {
  HighlightButton,
  SvgCheckBox,
  SvgIconExpandLess,
  SvgIconExpandMore,
  SvgTodo,
} from '@sqior/react/uibase';
import { SelectionPageContext } from '@sqior/react/uiselection';
import {
  WorkflowPathStepLabelType,
  WorkflowPathStepModel,
  WorkflowPathStepState,
} from '@sqior/viewmodels/workflow';
import { InterweaveExt } from '@sqior/react/uibase';
import { useContext, useEffect, useRef } from 'react';
import HorizontalLine from '../horizontal-line/horizontal-line';
import VerticalLine from '../vertical-line/vertical-line';
import { ReactComponent as ProblemIcon } from './problem.svg';
import styles from './workflow-path-step.module.css';
import { classes, getPrettyTimeOnlyString } from '@sqior/react/utils';
import WarningIcon from '@mui/icons-material/ReportOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import PresenceIcon from '@mui/icons-material/WhereToVoteOutlined';

export interface WorkflowPathStepProps {
  model: WorkflowPathStepModel;
  topLine: boolean;
  activeTop: boolean;
  bottomLine: boolean;
  timestamp: ClockTimestamp;
  additionalClassName?: string;
  folded?: boolean;
  setFold: (fold: boolean) => void;
  scrollToPosition?: (element: HTMLDivElement) => void;
}

const WorkflowPathCommentTypes = new Set<string>([
  WorkflowPathStepState.Comment,
  WorkflowPathStepState.AffirmativeComment,
  WorkflowPathStepState.ChallengedComment,
  WorkflowPathStepState.PresenceComment,
]);

const TextStyles = new Map<string, string>([
  [WorkflowPathStepState.Done, 'active-text'],
  [WorkflowPathStepState.Problem, 'problem-text'],
  [WorkflowPathStepState.Todo, 'text'],
  [WorkflowPathStepState.Comment, 'comment-text'],
  [WorkflowPathStepState.AffirmativeComment, 'icon-affirmative-text'],
  [WorkflowPathStepState.ChallengedComment, 'icon-challenged-text'],
  [WorkflowPathStepState.PresenceComment, 'icon-affirmative-text'],
]);
function textStyle(model: WorkflowPathStepModel) {
  return TextStyles.get(model.state) + (model.label.timestamp ? '-timestamp' : '');
}

export function WorkflowPathStep(props: WorkflowPathStepProps) {
  const dispatcher = useContext(OperationContext);
  const selContext = useContext(SelectionPageContext);
  const container = useRef<HTMLDivElement>(null);
  const FactoryComponent = useContext(ComponentFactory);

  /* Scroll to the active button, if applicable */
  const stepType = props.model.label.type;
  const stepState = props.model.state;
  useEffect(() => {
    if (
      stepType === WorkflowPathStepLabelType.Button &&
      stepState === WorkflowPathStepState.Todo &&
      container.current
    ) {
      props.scrollToPosition?.(container.current);
    }
  }, [stepType, stepState]);

  return (
    <div ref={container} className={styles['container']}>
      <div className={styles['icon-container']}>
        {props.topLine && (
          <VerticalLine className={styles[props.activeTop ? 'active-line' : 'line']} />
        )}
        {!props.topLine && <div className={styles['line']} />}
        {props.model.state === WorkflowPathStepState.Done && (
          <SvgCheckBox className={styles['active-icon']} />
        )}
        {props.model.state === WorkflowPathStepState.Problem && (
          <ProblemIcon className={styles['problem-icon']} />
        )}
        {props.model.state === WorkflowPathStepState.Todo && <SvgTodo className={styles['icon']} />}
        {props.bottomLine && !WorkflowPathCommentTypes.has(props.model.state) && (
          <VerticalLine
            className={
              styles[props.model.state === WorkflowPathStepState.Done ? 'active-line' : 'line']
            }
          />
        )}
        {!props.bottomLine && !WorkflowPathCommentTypes.has(props.model.state) && (
          <div className={styles['line']} />
        )}
      </div>
      <div
        className={classes(
          styles['text-container'],
          props.additionalClassName,
          props.model.state === WorkflowPathStepState.ChallengedComment
            ? styles['challenged-background']
            : undefined
        )}
        onClick={() => {
          if (props.folded !== undefined) props.setFold(!props.folded);
        }}
      >
        {props.model.state === WorkflowPathStepState.Problem && (
          <HorizontalLine className={styles['problem-line']} />
        )}
        {props.model.label.timestamp && (
          <div className={styles['timestamp']}>
            {getPrettyTimeOnlyString(props.model.label.timestamp)}
          </div>
        )}
        {props.model.state === WorkflowPathStepState.AffirmativeComment &&
          !props.model.label.timestamp && <DoneAllIcon className={styles['doneall-icon-small']} />}
        {props.model.state === WorkflowPathStepState.ChallengedComment &&
          !props.model.label.timestamp && <WarningIcon className={styles['problem-icon-small']} />}
        {props.model.label.type === WorkflowPathStepLabelType.Text && (
          <InterweaveExt
            className={styles[textStyle(props.model) ?? '']}
            content={props.model.label.text}
          />
        )}
        {props.model.state === WorkflowPathStepState.AffirmativeComment &&
          props.model.label.timestamp && (
            <DoneAllIcon className={styles['doneall-icon-small-after']} />
          )}
        {props.model.state === WorkflowPathStepState.PresenceComment &&
          props.model.label.timestamp && (
            <PresenceIcon className={styles['doneall-icon-small-after']} />
          )}
        {props.model.label.type === WorkflowPathStepLabelType.Text &&
          props.model.label.addText &&
          props.model.label.addText.length > 0 && (
            <span className={styles['add-text']}>{props.model.label.addText}</span>
          )}
        {props.model.label.type === WorkflowPathStepLabelType.Button && (
          <HighlightButton
            animated={true}
            additionalClassName={styles['button']}
            disabled={props.model.label.pending}
            secondary={props.model.state !== WorkflowPathStepState.Todo}
            onClick={() => {
              if (props.model.label.operation)
                if ('entityType' in props.model.label.operation)
                  selContext(props.model.label.operation, () => {
                    /* nothing to do */
                  });
                else dispatcher.start(props.model.label.operation);
            }}
          >
            {props.model.label.text}
          </HighlightButton>
        )}
        {props.model.label.type === WorkflowPathStepLabelType.Custom && props.model.label.custom && (
          <div className={styles['custom-container']}>
            <FactoryComponent data={props.model.label.custom} />
          </div>
        )}
        {props.folded === true && <SvgIconExpandMore className={styles['more-button']} />}
        {props.folded === false && <SvgIconExpandLess className={styles['less-button']} />}
      </div>
    </div>
  );
}

export default WorkflowPathStep;
